import { cleanText, cleanValue, sanitizeUrl } from "../lib/sanitize";
import type { ActivityType } from "./types";

export interface ClicksApi {
    track: (type: ActivityType, data: Record<string, unknown>) => void;
    noteClick: () => void;
    now?: () => number;
}

const INTERACTIVE = [
    "a",
    "button",
    "[role=button]",
    "[role=link]",
    "[role=tab]",
    "[role=menuitem]",
    "[role=option]",
    "[role=checkbox]",
    "[role=switch]",
    "input",
    "select",
    "textarea",
    "label",
    "summary",
    "[data-track]",
    "[data-track-item-id]",
].join(",");
const LANDMARK =
    "[data-track-section],[id],section[aria-label],nav,header,main,footer,[role=dialog]";
const FIELD_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);
// React зеркалит значение управляемого <textarea> в его текст (defaultValue),
// так что textContent обёртки с полем внутри содержит то, что человек набрал.
const TYPED_TEXT_INSIDE = "textarea,[contenteditable]:not([contenteditable=false])";
// Надпись варианта — это выбранное значение поля (ответ в анкете о здоровье).
const CHOICE =
    "label,input[type=radio],input[type=checkbox],[role=option],[role=radio],[role=checkbox],[role=switch],[role=menuitemradio],[role=menuitemcheckbox]";
const SENSITIVE_NAME_RE = /pass|card|cvc|cvv|iban|token|secret|otp|code/i;
const SENSITIVE_AUTOCOMPLETE = new Set(["current-password", "new-password", "one-time-code"]);
const LEN_TYPES = new Set(["text", "search", "email", "tel", "url", "number", "textarea"]);
const SKIP_TRACK_KEYS = new Set(["private", "ignore", "search"]);
const ADMIN_ROUTE_RE = /^\/(?:[a-z]{2}\/)?admin/;
const SEARCH_DEBOUNCE_MS = 1_500;
const REFOCUS_MS = 2_000;

const isAdminRoute = (): boolean => ADMIN_ROUTE_RE.test(window.location.pathname);

const elementOf = (t: EventTarget | null): Element | null =>
    t instanceof Element ? t : t instanceof Node ? t.parentElement : null;

const isField = (el: Element): boolean =>
    FIELD_TAGS.has(el.tagName) || (el as HTMLElement).isContentEditable === true;

/** Все data-track-* на элементе и шести предках; ближайший побеждает. */
const trackAttrs = (el: Element): Record<string, string> | undefined => {
    const out: Record<string, string> = {};
    let node: Element | null = el;
    for (let depth = 0; node && depth <= 6; depth++, node = node.parentElement) {
        const attrs: Attr[] = Array.from(node.attributes);
        for (const a of attrs) {
            if (!a.name.startsWith("data-track")) continue;
            const key: string =
                a.name === "data-track"
                    ? "name"
                    : a.name.slice("data-track-".length).replace(/-/g, "_");
            if (!key || SKIP_TRACK_KEYS.has(key) || key.startsWith("media_")) continue;
            if (!(key in out)) out[key] = cleanValue(a.value, 80);
        }
    }
    return Object.keys(out).length > 0 ? out : undefined;
};

const landmarkOf = (el: Element, privateZone: boolean): string | undefined => {
    const lm = el.parentElement?.closest(LANDMARK);
    if (!lm) return undefined;
    const section = lm.getAttribute("data-track-section");
    if (section) return cleanValue(section, 60);
    if (lm.id) return `#${cleanValue(lm.id, 60)}`;
    const role = lm.getAttribute("role");
    // В чате подпись окна — это часто имя собеседника.
    const aria = privateZone ? null : lm.getAttribute("aria-label");
    const base = role === "dialog" ? "dialog" : lm.tagName.toLowerCase();
    return cleanText(aria ? `${base}[${aria}]` : base, 60);
};

const pointerAncestor = (start: Element): Element | null => {
    let node: Element | null = start;
    for (let i = 0; node && i <= 4; i++, node = node.parentElement) {
        try {
            if (window.getComputedStyle(node).cursor === "pointer") return node;
        } catch {
            return null;
        }
    }
    return null;
};

export const describeClick = (el: Element, e?: MouseEvent): Record<string, unknown> => {
    const privateZone = !!el.closest("[data-track-private]");
    const choice = !!el.closest(CHOICE);
    const omitText =
        privateZone ||
        choice ||
        isField(el) ||
        isAdminRoute() ||
        !!el.closest(TYPED_TEXT_INSIDE) ||
        !!el.querySelector(TYPED_TEXT_INSIDE);
    const d: Record<string, unknown> = { tag: el.tagName.toLowerCase() };
    const role = el.getAttribute("role");
    if (role) d.role = cleanValue(role, 20);
    if (!omitText) {
        const text = cleanText(el.textContent, 80);
        if (text) d.text = text;
    }
    if (!privateZone && !choice) {
        const aria = el.getAttribute("aria-label");
        if (aria) d.aria = cleanText(aria, 80);
    }
    const href = el instanceof HTMLAnchorElement ? el.getAttribute("href") : null;
    if (href && !privateZone) d.href = sanitizeUrl(href);
    if (el.id && el.id.length <= 40) d.id = cleanValue(el.id, 40);
    const track = trackAttrs(el);
    if (track) d.track = track;
    const landmark = landmarkOf(el, privateZone);
    if (landmark) d.landmark = landmark;
    if (e && window.innerWidth > 0 && window.innerHeight > 0) {
        d.x_pct = Math.round((100 * e.clientX) / window.innerWidth);
        d.y_pct = Math.round((100 * e.clientY) / window.innerHeight);
    }
    return d;
};

const isSensitiveField = (el: Element): boolean => {
    const type = (el.getAttribute("type") || "").toLowerCase();
    const ac = (el.getAttribute("autocomplete") || "").toLowerCase();
    if (type === "password" || ac.startsWith("cc-") || SENSITIVE_AUTOCOMPLETE.has(ac)) return true;
    return SENSITIVE_NAME_RE.test(`${el.getAttribute("name") || ""} ${el.id || ""}`);
};

const formName = (form: Element | null): string | undefined => {
    if (!form) return undefined;
    const v =
        form.getAttribute("name") ||
        form.id ||
        form.getAttribute("aria-label") ||
        form.getAttribute("data-track");
    return v ? cleanValue(v, 40) : undefined;
};

const fieldType = (el: Element): string =>
    el instanceof HTMLInputElement ? (el.type || "text").toLowerCase() : el.tagName.toLowerCase();

/** Имя поля и его тип — никогда не значение. */
export const describeField = (el: Element, withLen: boolean): Record<string, unknown> => {
    const tag = el.tagName.toLowerCase();
    if (isSensitiveField(el)) return { sensitive: true, tag };
    const name =
        el.getAttribute("name") ||
        el.id ||
        el.getAttribute("aria-label") ||
        el.getAttribute("data-track") ||
        "";
    const type = fieldType(el);
    const d: Record<string, unknown> = { tag, type };
    if (name) d.field = cleanValue(name, 40);
    const form = formName(el.closest("form"));
    if (form) d.form = form;
    if (withLen && LEN_TYPES.has(type)) {
        const v =
            el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
                ? el.value
                : (el as HTMLElement).isContentEditable
                  ? el.textContent || ""
                  : null;
        if (v !== null) d.len = v.length;
    }
    return d;
};

/**
 * Клики, поля и формы. Всё — в фазе захвата: часть кнопок вызывает
 * stopPropagation (SpecialistOffer), и слушатель на всплытии их бы не увидел.
 */
export const installClicks = (api: ClicksApi): (() => void) => {
    const now = api.now || Date.now;
    let lastFocus: { el: Element; at: number } | null = null;
    const searchTimers = new Map<string, ReturnType<typeof setTimeout>>();
    const lastSearch = new Map<string, string>();

    const onClick = (e: MouseEvent) => {
        try {
            const t = elementOf(e.target);
            if (!t) return;
            const el = t.closest(INTERACTIVE) || pointerAncestor(t);
            if (!el) return;
            api.noteClick();
            api.track("click", describeClick(el, e));
        } catch {
            /* учёт не должен мешать клику */
        }
    };

    const onFocus = (e: FocusEvent) => {
        try {
            const el = elementOf(e.target);
            if (!el || !isField(el)) return;
            const t = now();
            if (lastFocus && lastFocus.el === el && t - lastFocus.at < REFOCUS_MS) return;
            lastFocus = { el, at: t };
            api.track("field_focus", describeField(el, false));
        } catch {
            /* ignore */
        }
    };

    const onChange = (e: Event) => {
        try {
            const el = elementOf(e.target);
            if (!el || !isField(el)) return;
            api.track("field_change", describeField(el, true));
        } catch {
            /* ignore */
        }
    };

    const onSubmit = (e: Event) => {
        try {
            const form = e.target;
            if (!(form instanceof HTMLFormElement)) return;
            api.track("form_submit", {
                form: formName(form),
                fields_n: form.elements.length,
            });
        } catch {
            /* ignore */
        }
    };

    // Значение пишем только у полей, явно помеченных data-track-search.
    const emitSearch = (el: HTMLInputElement | HTMLTextAreaElement, via: "debounce" | "enter") => {
        const surface = cleanValue(el.getAttribute("data-track-search"), 40);
        if (!surface) return;
        const query = cleanText(el.value, 150);
        if (!query || lastSearch.get(surface) === query) return;
        lastSearch.set(surface, query);
        api.track("search_input", { surface, query, len: el.value.length, via });
    };

    const searchField = (t: EventTarget | null): HTMLInputElement | HTMLTextAreaElement | null =>
        (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) &&
        t.hasAttribute("data-track-search") &&
        !isSensitiveField(t)
            ? t
            : null;

    const onInput = (e: Event) => {
        const el = searchField(e.target);
        if (!el) return;
        const surface = el.getAttribute("data-track-search") || "";
        const prev = searchTimers.get(surface);
        if (prev) clearTimeout(prev);
        searchTimers.set(
            surface,
            setTimeout(() => {
                searchTimers.delete(surface);
                try {
                    emitSearch(el, "debounce");
                } catch {
                    /* ignore */
                }
            }, SEARCH_DEBOUNCE_MS),
        );
    };

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== "Enter" || e.shiftKey || e.isComposing) return;
        const el = searchField(e.target);
        if (!el) return;
        const surface = el.getAttribute("data-track-search") || "";
        const prev = searchTimers.get(surface);
        if (prev) clearTimeout(prev);
        searchTimers.delete(surface);
        try {
            emitSearch(el, "enter");
        } catch {
            /* ignore */
        }
    };

    const opts: AddEventListenerOptions = { capture: true, passive: true };
    document.addEventListener("click", onClick, opts);
    document.addEventListener("focusin", onFocus, opts);
    document.addEventListener("change", onChange, opts);
    document.addEventListener("submit", onSubmit, opts);
    document.addEventListener("input", onInput, opts);
    document.addEventListener("keydown", onKeyDown, opts);
    return () => {
        document.removeEventListener("click", onClick, opts);
        document.removeEventListener("focusin", onFocus, opts);
        document.removeEventListener("change", onChange, opts);
        document.removeEventListener("submit", onSubmit, opts);
        document.removeEventListener("input", onInput, opts);
        document.removeEventListener("keydown", onKeyDown, opts);
        searchTimers.forEach((t) => clearTimeout(t));
        searchTimers.clear();
    };
};
